import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { RealtimeConnection, RealtimeEnvelope } from '@zayan-erp/realtime';
import { LocalStorageManagerService } from '@zayan-erp/shared';
import { ToastrService } from 'ngx-toastr';
import { filter, finalize, Observable, shareReplay } from 'rxjs';
import {
  DealLock,
  DealLockTakenOver,
  DealPresenceCommands,
  DealPresenceEvents,
  DealPresenceSnapshot,
  DealViewer
} from '../models/deal-presence-events';

/**
 * How often an editor re-claims the deal. Independent of the lease: the lease says how long a lock
 * survives silence, this says how often we prove someone is still there. Must stay well under the
 * server's DealPresence:LockMinutes, or a lock lapses before the first beat.
 */
const BEAT_SECONDS = 20;

export type LockAttempt = 'granted' | 'refused' | 'unavailable';

/** Who else is on the deal you have open, and who is editing it. One deal at a time. */
@Injectable({ providedIn: 'root' })
export class DealPresenceService {
  readonly #realtime = inject(RealtimeConnection);
  readonly #storage = inject(LocalStorageManagerService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #toastr = inject(ToastrService);

  #dealId: string | null = null;
  #lapseTimer: ReturnType<typeof setTimeout> | null = null;
  #heartbeat: ReturnType<typeof setInterval> | null = null;
  #takeOverPrompt$: Observable<boolean> | null = null;

  readonly #viewers = signal<DealViewer[]>([]);
  readonly #lock = signal<DealLock | null>(null);

  readonly others = computed(() => {
    // Ids come from two sources (token claim, login response), so do not trust the casing to match.
    const me = this.#me();
    return this.#viewers().filter(viewer => viewer.userId.toLowerCase() !== me);
  });

  readonly heldByMe = computed(() => this.#lock()?.userId.toLowerCase() === this.#me());
  readonly lockedByOther = computed(() => !!this.#lock() && !this.heldByMe());
  readonly holderName = computed(() => this.#lock()?.displayName ?? '');

  constructor() {
    this.#realtime.envelopes$.pipe(takeUntilDestroyed()).subscribe(envelope => this.#onEnvelope(envelope));

    toObservable(this.#realtime.isConnected)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => this.#send(DealPresenceCommands.watch));

    this.#destroyRef.onDestroy(() => this.#stopTimers());
  }

  get dealId(): string | null {
    return this.#dealId;
  }

  watch(dealId: string): void {
    this.#dealId = dealId;
    this.#viewers.set([]);
    this.#setLock(null);
    this.#send(DealPresenceCommands.watch);
  }

  unwatch(): void {
    this.#send(DealPresenceCommands.releaseLock);
    this.#send(DealPresenceCommands.unwatch);
    this.#dealId = null;
    this.#viewers.set([]);
    this.#setLock(null);
  }

  confirmTakeOver(ask: () => Observable<boolean>): Observable<boolean> {
    this.#takeOverPrompt$ ??= ask().pipe(
      finalize(() => (this.#takeOverPrompt$ = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.#takeOverPrompt$;
  }

  async tryAcquireLock(): Promise<LockAttempt> {
    if (!this.#dealId || !this.#realtime.isConnected()) return 'unavailable';
    if (this.heldByMe()) return 'granted';

    return this.#claim(DealPresenceCommands.acquireLock);
  }

  async takeOverLock(): Promise<LockAttempt> {
    if (!this.#dealId || !this.#realtime.isConnected()) return 'unavailable';

    return this.#claim(DealPresenceCommands.takeOverLock);
  }

  async #claim(command: string): Promise<LockAttempt> {
    try {
      await this.#realtime.invoke('Send', command, { dealId: this.#dealId });
      return 'granted';
    } catch {
      // Only the server answering means someone else holds it. A dropped socket answers nothing,
      // and treating that as a refusal shows a take-over offer that cannot work.
      return this.#realtime.isConnected() ? 'refused' : 'unavailable';
    }
  }

  #onEnvelope(envelope: RealtimeEnvelope): void {
    if (envelope.type === DealPresenceEvents.takenOver)
      return this.#onTakenOver(envelope.payload as DealLockTakenOver | undefined);

    if (envelope.type !== DealPresenceEvents.snapshot) return;

    const snapshot = envelope.payload as DealPresenceSnapshot | undefined;

    // A snapshot for a deal we have since left would otherwise overwrite the current one.
    if (!snapshot || snapshot.dealId !== this.#dealId) return;

    this.#viewers.set(snapshot.viewers ?? []);
    this.#setLock(snapshot.lock ?? null);
  }

  #onTakenOver(payload: DealLockTakenOver | undefined): void {
    if (!payload || payload.dealId !== this.#dealId) return;

    this.#setLock(null);
    // Sticky until closed, so a user who was away still sees it; the chip shows who is editing now.
    this.#toastr.warning(`${payload.takenOverBy} has taken control of this deal.`, undefined, {
      disableTimeOut: true,
      closeButton: true,
      tapToDismiss: false
    });
  }

  #setLock(lock: DealLock | null): void {
    this.#lock.set(lock);
    this.#resetLapseTimer(lock);
    this.#syncHeartbeat(lock);
  }

  #resetLapseTimer(lock: DealLock | null): void {
    if (this.#lapseTimer !== null) clearTimeout(this.#lapseTimer);

    this.#lapseTimer = lock ? setTimeout(() => this.#lock.set(null), lock.expiresInSeconds * 1_000) : null;
  }

  #syncHeartbeat(lock: DealLock | null): void {
    if (!lock || lock.userId.toLowerCase() !== this.#me()) {
      this.#stopHeartbeat();
      return;
    }

    this.#heartbeat ??= setInterval(() => this.#beat(), BEAT_SECONDS * 1_000);
  }

  // Claims directly: extending is the whole job, so tryAcquireLock's "already mine" exit would skip it.
  // Only while the tab is in front - a backgrounded tab is not someone editing.
  #beat(): void {
    if (document.hasFocus()) void this.#claim(DealPresenceCommands.acquireLock);
  }

  #stopTimers(): void {
    if (this.#lapseTimer !== null) clearTimeout(this.#lapseTimer);
    this.#lapseTimer = null;
    this.#stopHeartbeat();
  }

  #stopHeartbeat(): void {
    if (this.#heartbeat !== null) clearInterval(this.#heartbeat);
    this.#heartbeat = null;
  }

  #me(): string {
    return this.#storage.getUserIdFromStorage()?.toLowerCase() ?? '';
  }

  #send(command: string): void {
    if (!this.#dealId || !this.#realtime.isConnected()) return;

    // Presence is decoration: a failed send must never surface to the user.
    this.#realtime
      .invoke('Send', command, { dealId: this.#dealId })
      .catch((error: unknown) => console.warn('Realtime: deal presence command failed.', error));
  }
}
